import React from 'react'
import I18n from 'i18n'
import Field from '../common/Field'
import { MyAxios as axios } from '../../../../MyAxios'
import { readGenericQuestionnaire } from '../../../../common/utils/QuestionnaireUtils'
import { WithQuestionnaires } from '../../../../questionnaires/QuestionnaireProvider'

interface KccqData {
  v1a?: string
  v1b?: string
  v1c?: string
  v2?: string
  v3?: string
  v4?: string
  v5?: string
  v6?: string
  v7?: string
  v8a?: string
  v8b?: string
  v8c?: string
}

interface QuestionnaireDefinition {
  content?: {
    questions?: Array<{
      id?: string
    }>
  }
}

interface Questionnaires {
  questionnaireDefinitionUrl: (questionnaireKey: string) => string
  authenticatedHeaders: () => Record<string, string>
}

type KccqQuestionId = 'v1a' | 'v1b' | 'v1c' | 'v2' | 'v3'

interface KccqProps {
  data: {
    values: KccqData
    questionnaire?: {
      key?: string
    }
  }
  questionnaires?: Questionnaires
}

interface KccqState {
  questionnaireDefinition: QuestionnaireDefinition | null
}

class Kccq extends React.Component<KccqProps, KccqState> {
  constructor (props: KccqProps) {
    super(props)
    this.state = {
      questionnaireDefinition: null
    }
  }

  componentDidMount (): void {
    this.fetchQuestionnaireDefinition()
  }

  componentDidUpdate (prevProps: KccqProps): void {
    const previousQuestionnaireKey = prevProps.data?.questionnaire?.key
    const questionnaireKey = this.props.data?.questionnaire?.key
    if (previousQuestionnaireKey !== questionnaireKey) {
      this.fetchQuestionnaireDefinition()
    }
  }

  fetchQuestionnaireDefinition (): void {
    const questionnaireKey = this.props.data?.questionnaire?.key
    const questionnaireDefinitionUrl = this.props.questionnaires?.questionnaireDefinitionUrl

    if (questionnaireKey == null || questionnaireKey === '' || questionnaireDefinitionUrl == null) return

    axios({
      method: 'GET',
      url: questionnaireDefinitionUrl(questionnaireKey),
      headers: { ...this.props.questionnaires?.authenticatedHeaders(), Accept: 'application/json' }
    }).then((response) => {
      this.setState({ questionnaireDefinition: response.data })
    }).catch((err) => {
      console.log('Error retrieving KCCQ questionnaire definition')
      console.log(err)
    })
  }

  translatedValue (questionId: KccqQuestionId): string | number | undefined {
    const value = this.props.data.values[questionId]

    if (value === null || value === undefined || value === '') return value

    const questionnaireDefinition = this.state.questionnaireDefinition
    if (questionnaireDefinition == null || questionnaireDefinition.content == null || !Array.isArray(questionnaireDefinition.content.questions)) {
      return value
    }

    const questionDefinition = questionnaireDefinition.content.questions.find(question => question.id === questionId)
    if (questionDefinition == null) return value

    const localizedQuestionAndAnswer = readGenericQuestionnaire(
      this.props.data,
      {
        ...questionnaireDefinition,
        content: {
          ...questionnaireDefinition.content,
          questions: [questionDefinition]
        }
      },
      I18n.locale
    )

    return localizedQuestionAndAnswer?.[0]?.value ?? value
  }

  render (): React.ReactElement {
    const v1a = this.translatedValue('v1a')
    const v1b = this.translatedValue('v1b')
    const v1c = this.translatedValue('v1c')
    const v2 = this.translatedValue('v2')
    const v3 = this.translatedValue('v3')

    return (
      <>
        <div className='sport-fields'>
          {/* Question 1: Table questions for physical limitations */}
          {Boolean(v1a) && (
            <Field
              title={I18n.t('questionnaires.kccq.v1a')}
              value={v1a}
            />
          )}

          {Boolean(v1b) && (
            <Field
              title={I18n.t('questionnaires.kccq.v1b')}
              value={v1b}
            />
          )}

          {Boolean(v1c) && (
            <Field
              title={I18n.t('questionnaires.kccq.v1c')}
              value={v1c}
            />
          )}

          {/* Question 2: Swelling */}
          {Boolean(v2) && (
            <Field title={I18n.t('questionnaires.kccq.v2')} value={v2} />
          )}

          {/* Question 3: Fatigue */}
          {Boolean(v3) && (
            <Field title={I18n.t('questionnaires.kccq.v3')} value={v3} />
          )}
        </div>
      </>
    )
  }
}

export default WithQuestionnaires(Kccq)
